<script>
  import DashboardLayout from "../../layouts/Dashboard.svelte";
  import { onMount } from "svelte";
  import { Chart, registerables } from "chart.js";
  import { makeApiRequest } from "../../utils/ajax";

  import Header from "../../app/social_profile/Header.svelte";
  import MetricFollowers from "../../app/social_profile/MetricFollowers.svelte";
  import MetricFollowings from "../../app/social_profile/MetricFollowings.svelte";
  import MetricPosts from "../../app/social_profile/MetricPosts.svelte";
  import MetricEngagementScore from "../../app/social_profile/MetricEngagementScore.svelte";
  import RecentPosts from "../../app/social_profile/RecentPosts.svelte";
  import RecentReels from "../../app/social_profile/RecentReels.svelte";
  import RecentStories from "../../app/social_profile/RecentStories.svelte";
  import RecentActivity from "../../app/recent_activity/RecentActivity.svelte";

  let { id } = $props();

  let socialProfile = $state(null);
  let profile = $state(null);
  let posts = $state([]);
  let reels = $state([]);
  let stories = $state([]);

  let loading = $state(true);
  let errorred = $state(false);

  let lastApiData = $state({});

  let stillScraping = $derived(socialProfile?.last_scanned === null);
  let viewAllPosts = $state(false);
  let viewAllReels = $state(false);

  async function loadSocialProfile(showLoading = true) {
    if (!id) return;

    if (showLoading) {
      loading = true;
    }

    try {
      const data = await makeApiRequest(`/api/social_profiles/${id}`);

      if (data.success === false) {
        throw new Error("Failed to load social profile");
      }

      socialProfile = data.socialProfile;
      profile = data.profile;
      posts = data.posts || [];
      reels = data.reels || [];
      stories = data.stories || [];

      lastApiData = data;
    } catch (error) {
      errorred = true;
    } finally {
      loading = false;
    }
  }

  function formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num;
  }

  function chartDateLabels(dates) {
    if (!dates || dates.length === 0) return [];

    const firstDate = new Date(dates[0]);
    const lastDate = new Date(dates[dates.length - 1]);
    const daysDifference = (lastDate - firstDate) / (1000 * 60 * 60 * 24);

    let timeScale;

    if (daysDifference < 1) {
      timeScale = "hour";
    } else if (daysDifference <= 7) {
      timeScale = "day";
    } else if (daysDifference <= 60) {
      timeScale = "week";
    } else {
      timeScale = "month";
    }

    return dates.map((date) => {
      const dateObj = new Date(date);

      switch (timeScale) {
        case "hour":
          return dateObj
            .toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
            .replace(/\s/g, " ");

        case "day":
          return dateObj.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          });

        case "week":
          // For weekly, show the date of the beginning of the week
          return dateObj.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          });

        case "month":
          return dateObj.toLocaleDateString([], {
            month: "short",
            year: "numeric",
          });

        default:
          return dateObj.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          });
      }
    });
  }

  function togglePosts(e) {
    e.preventDefault();
    viewAllPosts = !viewAllPosts;
  }

  function toggleReels(e) {
    e.preventDefault();
    viewAllReels = !viewAllReels;
  }

  onMount(async () => {
    Chart.register(...registerables);
    await loadSocialProfile();

    let refreshInterval = setInterval(
      async () => loadSocialProfile(false),
      5000
    );

    return () => {
      clearInterval(refreshInterval);
    };
  });
</script>

<DashboardLayout container={false}>
  {#snippet content()}
    {#if errorred}
      {@render showError()}
    {:else if loading}
      {@render loadingSpinner()}
    {:else}
      <!-- Profile Header -->
      <div class="bg-white shadow rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
        <Header {socialProfile} {profile} />
      </div>

      <!-- Key Metrics -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <MetricFollowers
          {profile}
          {formatNumber}
          data={lastApiData}
          {chartDateLabels}
        />
        <MetricFollowings
          {profile}
          {formatNumber}
          data={lastApiData}
          {chartDateLabels}
        />
        <MetricPosts
          {profile}
          {formatNumber}
          data={lastApiData}
          {chartDateLabels}
        />
        <MetricEngagementScore {profile} {formatNumber} />
      </div>

      <!-- Main Content Area -->
      {#if profile.is_private}
        {@render showPrivateProfile()}
      {:else}
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <!-- Content Section (2/3 width) -->
          <div class="lg:col-span-2 space-y-4 sm:space-y-6">
            {#if stillScraping || posts.length > 0}
              <RecentPosts {posts} {stillScraping} />
            {/if}

            {#if stillScraping || reels.length > 0}
              <RecentReels {reels} {stillScraping} {formatNumber} />
            {/if}

            {#if stillScraping || stories.length > 0}
              <RecentStories {stories} {stillScraping} {formatNumber} />
            {/if}

            {#if !stillScraping && posts.length === 0 && reels.length === 0 && stories.length === 0}
              <div class="bg-white shadow rounded-lg p-6 sm:p-8 text-center">
                <div
                  class="bg-gray-100 p-3 sm:p-4 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6 sm:h-8 sm:w-8 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 class="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  No Content Available
                </h3>
                <p class="text-gray-500 text-sm sm:text-base">
                  This profile doesn't have any posts, reels, or stories yet.
                </p>
              </div>
            {/if}
          </div>

          <!-- Activity Feed Section (1/3 width) -->
          <div class="lg:col-span-1">
            <div class="bg-white shadow rounded-lg p-4 sm:p-6 sticky top-6">
              <RecentActivity socialProfileId={id} visibleCount={8} />
            </div>
          </div>
        </div>
      {/if}
    {/if}
  {/snippet}
</DashboardLayout>

{#snippet showPrivateProfile()}
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
    <!-- Private Profile Message (2/3 width) -->
    <div class="lg:col-span-2">
      <div
        class="bg-white shadow rounded-lg p-6 sm:p-8 text-center h-full flex flex-col justify-center"
      >
        <div
          class="bg-indigo-100 p-4 sm:p-6 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-8 w-8 sm:h-10 sm:w-10 text-indigo-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <h2 class="text-xl sm:text-2xl font-bold text-gray-800 mb-4">
          Private Profile
        </h2>
        <div
          class="h-1 w-20 sm:w-24 bg-indigo-200 mx-auto mb-4 sm:mb-6 rounded-full"
        ></div>
        <p
          class="text-gray-600 text-sm sm:text-lg max-w-md mx-auto mb-4 sm:mb-6 leading-relaxed"
        >
          Since this profile is set to private, we are unable to access their
          posts, reels, and stories. This also means we cannot compute
          engagement statistics or analyze content performance.
        </p>
        <div
          class="bg-indigo-50 border border-indigo-200 px-4 sm:px-6 py-3 sm:py-4 rounded-lg inline-block"
        >
          <span class="font-semibold text-indigo-700 text-sm sm:text-base"
            >Note:</span
          >
          <span class="text-indigo-600 ml-1 text-sm sm:text-base"
            >Only public profiles can be fully analyzed.</span
          >
        </div>
      </div>
    </div>

    <!-- Activity Feed Section (1/3 width) -->
    <div class="lg:col-span-1">
      <div class="bg-white shadow rounded-lg p-4 sm:p-6 sticky top-6">
        <RecentActivity socialProfileId={id} visibleCount={6} />
      </div>
    </div>
  </div>
{/snippet}

{#snippet loadingSpinner()}
  <div class="flex flex-col justify-center items-center h-full mt-8 sm:mt-14">
    <div class="relative">
      <div
        class="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-gray-200"
      ></div>
      <div
        class="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-t-4 border-indigo-600 absolute top-0 left-0"
      ></div>
    </div>
    <p class="mt-4 sm:mt-6 text-gray-600 font-medium text-sm sm:text-base">
      Loading data...
    </p>
    <div
      class="mt-3 sm:mt-4 bg-gray-200 h-1 w-24 sm:w-32 rounded-full overflow-hidden"
    >
      <div class="bg-indigo-600 h-1 w-1/2 rounded-full animate-pulse"></div>
    </div>
  </div>
{/snippet}

{#snippet showError()}
  <div class="flex flex-col justify-center items-center h-full mt-8 sm:mt-14">
    <div
      class="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 p-6 sm:p-8 rounded-lg shadow-lg max-w-2xl mx-auto"
    >
      <div class="flex flex-col md:flex-row items-center">
        <div class="bg-red-100 p-3 sm:p-4 rounded-full mb-4 md:mb-0 md:mr-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-8 w-8 sm:h-12 sm:w-12 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div class="text-center md:text-left">
          <h3 class="text-lg sm:text-xl font-bold text-red-700">
            Unable to Load Data
          </h3>
          <div
            class="h-1 w-16 sm:w-20 bg-red-200 my-2 sm:my-3 mx-auto md:mx-0 rounded-full"
          ></div>
          <p class="text-red-600 font-medium mt-3 sm:mt-5 text-sm sm:text-base">
            We encountered an error while loading this social profile.
          </p>
          <p
            class="text-gray-600 mt-2 sm:mt-3 mb-4 sm:mb-5 text-sm sm:text-base"
          >
            This could be due to a temporary issue. Please try refreshing the
            page or come back later.
          </p>
          <div class="flex flex-col sm:flex-row gap-3 mt-6 sm:mt-8">
            <button
              class="cursor-pointer bg-red-600 hover:bg-red-700 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 ease-in-out flex items-center justify-center shadow-md hover:shadow-lg text-sm sm:text-base"
              onclick={() => window.location.reload()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 sm:h-5 sm:w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh Page
            </button>
            <button
              class="cursor-pointer border border-gray-300 hover:border-gray-400 bg-white text-gray-700 hover:text-gray-900 py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 ease-in-out flex items-center justify-center shadow-sm hover:shadow text-sm sm:text-base"
              onclick={() => history.back()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 sm:h-5 sm:w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
{/snippet}
